import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import '@/App.css';

import Navbar from './components/Navbar';
import HomePage from './pages/HomePage';

import ListingDetail from './pages/ListingDetail';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import OnboardingPage from './pages/OnboardingPage';
import StartupDashboard from './pages/StartupDashboard';
import StartupCheckout from './pages/StartupCheckout';
import WearerDashboard from './pages/WearerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import VerifyPage from './pages/VerifyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F6F1E8' }}>
        <div className="animate-spin w-8 h-8 border-4 border-[#121212] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!user.onboarded && requiredRole) return <Navigate to="/onboarding" replace />;
  if (requiredRole && user.role !== requiredRole && user.role !== 'admin') return <Navigate to="/" replace />;

  return children;
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Detect session token during render, NOT in useEffect
  // This synchronous check prevents race conditions with AuthProvider
  if (location.hash?.includes('session_id=') || location.hash?.includes('session_token=')) {
    return <AuthCallback />;
  }

  return (
    <>
      {/* Show Navbar on all pages except login, onboarding, and auth callback */}
      {!['/login', '/onboarding'].includes(location.pathname) && <Navbar />}
      <Routes>
        <Route path="/" element={<HomePage />} />

        <Route path="/listing/:listingId" element={<ListingDetail />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/onboarding" element={
          <ProtectedRoute><OnboardingPage /></ProtectedRoute>
        } />
        <Route path="/dashboard/startup" element={
          <ProtectedRoute requiredRole="startup"><StartupDashboard /></ProtectedRoute>
        } />
        <Route path="/dashboard/startup/checkout" element={
          <ProtectedRoute requiredRole="startup"><StartupCheckout /></ProtectedRoute>
        } />
        <Route path="/my-tshirt" element={
          <ProtectedRoute><WearerDashboard /></ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute requiredRole="admin"><AdminDashboard /></ProtectedRoute>
        } />
        <Route path="/verify/:token" element={
          <ProtectedRoute requiredRole="admin"><VerifyPage /></ProtectedRoute>
        } />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
